import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ToastMessage } from 'primereact/toast';

import { ToastContext } from '@/components/common/CommonToast';
import { useTranslation } from '@/config/i18n';
import { toastErrorMessage, toastMessage } from '@/lib/utils';
import { apiRequest } from '@/services';
import { SyncStatus, SyncStatusResponse } from '@/types/graduation';

// Rationale: openspec/changes/add-course-permission-sync-status-ui/design.md

const POLL_DELAY_MS = 4000;

const MAX_UNREACHABLE_POLLS = 3;

type SyncDoneContext = {
  // Where this watch came from, which is what decides where the admin lands: a watch the
  // admin started by saving ends on the list, a watch that only reports a run already in
  // flight leaves them where they are. The final status alone cannot tell those apart.
  startedBySave: boolean;
};

type UseCoursePermissionSyncProps = {
  graduationId?: number;
  onDone?: (context: SyncDoneContext) => void;
  delay?: number;
};

const useCoursePermissionSync = ({
  graduationId,
  onDone,
  delay = POLL_DELAY_MS,
}: UseCoursePermissionSyncProps) => {
  const [t] = useTranslation('');
  const { toast } = useContext(ToastContext);
  const { apiService } = apiRequest();

  const [isSyncing, setIsSyncing] = useState(false);

  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const unreachableCountRef = useRef(0);
  const watchIdRef = useRef(0);
  const watchingRef = useRef(false);
  const sawRunningRef = useRef(false);
  const startedBySaveRef = useRef(false);
  const failedToastRef = useRef<ToastMessage | null>(null);
  const onDoneRef = useRef(onDone);

  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  const cleanup = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const announceFailed = useCallback(() => {
    if (failedToastRef.current) return;

    const message: ToastMessage = {
      ...toastErrorMessage(t('graduation.permission_sync_failed')),
      severity: 'error',
      sticky: true,
    };

    failedToastRef.current = message;
    toast?.current?.show(message);
  }, [t, toast]);

  const dismissFailed = useCallback(() => {
    if (!failedToastRef.current) return;

    toast?.current?.remove?.(failedToastRef.current);
    failedToastRef.current = null;
  }, [toast]);

  const poll = useCallback(
    async (id: number, watchId: number) => {
      if (watchId !== watchIdRef.current) return;

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const { data } = await apiService.graduations.member<{
        data: SyncStatusResponse;
      }>(id, 'permission-sync-status', {
        signal: controller.signal,
      });

      if (watchId !== watchIdRef.current) return;

      const status = data?.data?.status;

      if (status === undefined) {
        unreachableCountRef.current += 1;

        if (unreachableCountRef.current < MAX_UNREACHABLE_POLLS) {
          timeoutIdRef.current = setTimeout(() => poll(id, watchId), delay);
          return;
        }

        watchingRef.current = false;
        setIsSyncing(false);
        toast?.current?.show(
          toastErrorMessage(t('graduation.permission_sync_check_failed')),
        );
        return;
      }

      unreachableCountRef.current = 0;

      if (status === 'running') {
        sawRunningRef.current = true;
        timeoutIdRef.current = setTimeout(() => poll(id, watchId), delay);
        return;
      }

      watchingRef.current = false;
      setIsSyncing(false);

      if (status === 'done') {
        // Toast first: onDone may navigate away, and the notice belongs to the run that
        // just ended, not to whatever screen the admin lands on.
        if (sawRunningRef.current) {
          toast?.current?.show(toastMessage(t('graduation.permission_sync_done')));
        }

        onDoneRef.current?.({ startedBySave: startedBySaveRef.current });

        return;
      }

      announceFailed();
    },
    [announceFailed, apiService, delay, t, toast],
  );

  const pollRef = useRef(poll);

  useEffect(() => {
    pollRef.current = poll;
  }, [poll]);

  const beginWatch = useCallback(
    (id: number, startedBySave: boolean) => {
      cleanup();
      const watchId = (watchIdRef.current += 1);
      watchingRef.current = true;
      unreachableCountRef.current = 0;
      dismissFailed();
      setIsSyncing(true);
      sawRunningRef.current = !startedBySave;
      startedBySaveRef.current = startedBySave;

      pollRef.current(id, watchId);
    },
    [cleanup, dismissFailed],
  );

  const startWatching = useCallback(() => {
    if (!graduationId) return;

    beginWatch(graduationId, true);
  }, [beginWatch, graduationId]);

  const applyDetailStatus = useCallback(
    (status?: SyncStatus) => {
      if (!graduationId) return;

      if (status === 'running') {
        if (watchingRef.current) return;

        beginWatch(graduationId, false);
        return;
      }

      if (status === 'failed') {
        announceFailed();
      }

    },
    [announceFailed, beginWatch, graduationId],
  );

  useEffect(() => {
    return () => {
      watchIdRef.current += 1;
      watchingRef.current = false;
      cleanup();
      dismissFailed();
      setIsSyncing(false);
    };
  }, [graduationId, cleanup, dismissFailed]);

  return {
    isSyncing,
    startWatching,
    applyDetailStatus,
  };
};

export default useCoursePermissionSync;
